import PageLayout from "../components/PageLayout";
import { CONTACT } from "../utils/contact";
import "./LegalPage.css";

const TOC = [
  { id: "short-version", label: "The short version" },
  { id: "information-collect", label: "Information We Collect" },
  { id: "legal-basis", label: "Our Legal Basis for Using Your Personal Information" },
  { id: "how-we-use", label: "How Do We Use The Information Collected?" },
  { id: "retention", label: "How Long Do We Keep Your Information?" },
  { id: "sharing", label: "Sharing Information" },
  { id: "cookies", label: "Cookies" },
  { id: "security", label: "Security" },
  { id: "your-rights", label: "Your Rights" },
  { id: "eu-users", label: "EU, EEA, and UK Users" },
  { id: "us-users", label: "US Users" },
  { id: "israeli-users", label: "Israeli Users" },
  { id: "updates", label: "Updates to This Policy" },
  { id: "contact", label: "Contact Us" },
];

export default function PrivacyPolicy() {
  return (
    <PageLayout title="Privacy Policy" wide>
      <p className="legal-updated">
        <strong>Last updated:</strong> June 12, 2026
      </p>

      <nav className="legal-toc" aria-label="Table of contents">
        <h2>Table of Contents</h2>
        <ol>
          {TOC.map((item, i) => (
            <li key={item.id}>
              <a href={`#${item.id}`}>
                {i + 1}. {item.label}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <section id="short-version">
        <h2>1. The short version</h2>
        <p>
          At Maskanh, we are committed to protecting your privacy and ensuring
          the security of your personal information. This privacy policy explains
          how we collect, use, and protect your data when you use our
          construction and home improvement services platform. We collect
          information you provide directly, information we collect automatically
          when you use our services, and information from third parties. We use
          this information to provide, maintain, and improve our services,
          process transactions, and communicate with you.
        </p>
      </section>

      <section id="information-collect">
        <h2>2. Information We Collect</h2>

        <h3>2.1 Information You Provide Directly</h3>
        <ul>
          <li>
            <strong>Account Information:</strong> When you create an account with
            Maskanh, we collect your name, email address, phone number, and
            password.
          </li>
          <li>
            <strong>Profile Information:</strong> We collect information about
            your construction projects, service preferences, location, and
            professional qualifications.
          </li>
          <li>
            <strong>Service Requests:</strong> When you request construction
            services, we collect details about your project requirements,
            budget, timeline, and property information.
          </li>
          <li>
            <strong>Communication:</strong> We collect information when you
            communicate with us, including customer support inquiries and
            feedback.
          </li>
          <li>
            <strong>Payment Information:</strong> We collect payment card
            information and billing addresses for processing transactions.
          </li>
        </ul>

        <h3>2.2 Information We Collect Automatically</h3>
        <ul>
          <li>
            <strong>Usage Information:</strong> We collect information about how
            you use our platform, including pages visited, time spent, and
            features used.
          </li>
          <li>
            <strong>Device Information:</strong> We collect information about
            your device, including IP address, browser type, operating system,
            and device identifiers.
          </li>
          <li>
            <strong>Location Information:</strong> We collect your approximate
            location to match you with local service providers and show relevant
            services.
          </li>
          <li>
            <strong>Cookies and Similar Technologies:</strong> We use cookies
            and similar technologies to enhance your experience and analyze
            platform usage.
          </li>
        </ul>

        <h3>2.3 Information from Third Parties</h3>
        <ul>
          <li>
            <strong>Social Media:</strong> If you connect your social media
            accounts, we may collect information from those platforms.
          </li>
          <li>
            <strong>Service Providers:</strong> We may receive information from
            third-party service providers who help us verify identities and
            process payments.
          </li>
          <li>
            <strong>Public Records:</strong> We may collect publicly available
            information about properties and construction projects.
          </li>
        </ul>
      </section>

      <section id="legal-basis">
        <h2>3. Our Legal Basis for Using Your Personal Information</h2>
        <p>We process your personal information based on the following legal grounds:</p>
        <ul>
          <li>
            <strong>Contract Performance:</strong> To provide our construction
            services platform and fulfill our contractual obligations.
          </li>
          <li>
            <strong>Legitimate Interests:</strong> To improve our services,
            prevent fraud, and ensure platform security.
          </li>
          <li>
            <strong>Consent:</strong> When you have given us explicit consent to
            process your information for specific purposes.
          </li>
          <li>
            <strong>Legal Compliance:</strong> To comply with applicable laws and
            regulations in the construction industry.
          </li>
        </ul>
      </section>

      <section id="how-we-use">
        <h2>4. How Do We Use The Information Collected?</h2>
        <p>We use your information for the following purposes:</p>
        <ul>
          <li>
            <strong>Service Provision:</strong> To connect you with qualified
            construction professionals and service providers.
          </li>
          <li>
            <strong>Platform Operation:</strong> To maintain, operate, and
            improve our construction services platform.
          </li>
          <li>
            <strong>Communication:</strong> To send you updates about your
            projects, service recommendations, and platform notifications.
          </li>
          <li>
            <strong>Payment Processing:</strong> To process payments for
            services and handle billing inquiries.
          </li>
          <li>
            <strong>Customer Support:</strong> To provide customer service and
            respond to your inquiries.
          </li>
          <li>
            <strong>Safety and Security:</strong> To ensure platform safety,
            prevent fraud, and protect against unauthorized access.
          </li>
          <li>
            <strong>Analytics:</strong> To analyze platform usage and improve
            our services.
          </li>
          <li>
            <strong>Marketing:</strong> To send you relevant construction tips,
            service updates, and promotional offers (with your consent).
          </li>
        </ul>
      </section>

      <section id="retention">
        <h2>5. How Long Do We Keep Your Information?</h2>
        <p>
          We retain your personal information for as long as necessary to
          provide our services and fulfill the purposes outlined in this policy:
        </p>
        <ul>
          <li>
            <strong>Account Information:</strong> We retain your account
            information for as long as your account is active and for a
            reasonable period thereafter.
          </li>
          <li>
            <strong>Project Information:</strong> We retain project details for
            the duration of the project and for legal and business purposes.
          </li>
          <li>
            <strong>Payment Information:</strong> We retain payment information
            as required by law and for accounting purposes.
          </li>
          <li>
            <strong>Communication Records:</strong> We retain customer service
            communications for quality assurance and legal purposes.
          </li>
          <li>
            <strong>Marketing Data:</strong> We retain marketing preferences
            until you opt out or request deletion.
          </li>
        </ul>
      </section>

      <section id="sharing">
        <h2>6. Sharing Information</h2>
        <p>We may share your information in the following circumstances:</p>
        <ul>
          <li>
            <strong>Service Providers:</strong> With construction professionals
            and service providers to facilitate your projects.
          </li>
          <li>
            <strong>Business Partners:</strong> With trusted partners who help us
            provide our services.
          </li>
          <li>
            <strong>Legal Requirements:</strong> When required by law or to
            protect our rights and safety.
          </li>
          <li>
            <strong>Business Transfers:</strong> In connection with mergers,
            acquisitions, or other business transactions.
          </li>
          <li>
            <strong>Consent:</strong> When you have given us explicit consent to
            share your information.
          </li>
        </ul>
      </section>

      <section id="cookies">
        <h2>7. Cookies</h2>
        <p>
          We use cookies and similar technologies to enhance your experience on
          our platform:
        </p>
        <ul>
          <li>
            <strong>Essential Cookies:</strong> Required for basic platform
            functionality and security.
          </li>
          <li>
            <strong>Analytics Cookies:</strong> Help us understand how you use
            our platform to improve our services.
          </li>
          <li>
            <strong>Preference Cookies:</strong> Remember your settings and
            preferences.
          </li>
          <li>
            <strong>Marketing Cookies:</strong> Used to deliver relevant
            advertisements and measure their effectiveness.
          </li>
        </ul>
        <p>
          You can control cookie settings through your browser preferences.
        </p>
      </section>

      <section id="security">
        <h2>8. Security</h2>
        <p>
          We implement appropriate technical and organizational measures to
          protect your personal information:
        </p>
        <ul>
          <li>
            <strong>Data Encryption:</strong> We use encryption to protect data
            in transit and at rest.
          </li>
          <li>
            <strong>Access Controls:</strong> We limit access to personal
            information to authorized personnel only.
          </li>
          <li>
            <strong>Regular Audits:</strong> We conduct regular security audits
            and assessments.
          </li>
          <li>
            <strong>Incident Response:</strong> We have procedures in place to
            respond to security incidents.
          </li>
          <li>
            <strong>Staff Training:</strong> We train our staff on data
            protection and security best practices.
          </li>
        </ul>
      </section>

      <section id="your-rights">
        <h2>9. Your Rights</h2>
        <p>You have the following rights regarding your personal information:</p>
        <ul>
          <li>
            <strong>Access:</strong> Request access to your personal information.
          </li>
          <li>
            <strong>Correction:</strong> Request correction of inaccurate or
            incomplete information.
          </li>
          <li>
            <strong>Deletion:</strong> Request deletion of your personal
            information.
          </li>
          <li>
            <strong>Portability:</strong> Request a copy of your data in a
            portable format.
          </li>
          <li>
            <strong>Restriction:</strong> Request restriction of processing in
            certain circumstances.
          </li>
          <li>
            <strong>Objection:</strong> Object to processing based on legitimate
            interests.
          </li>
          <li>
            <strong>Withdraw Consent:</strong> Withdraw consent for processing
            based on consent.
          </li>
        </ul>
        <p>
          To exercise these rights, please contact us using the information
          provided in the Contact Us section.
        </p>
      </section>

      <section id="eu-users">
        <h2>10. EU, EEA, and UK Users</h2>
        <p>
          If you are located in the European Union, European Economic Area, or
          United Kingdom, you have additional rights under the General Data
          Protection Regulation (GDPR):
        </p>
        <ul>
          <li>Right to be informed about data processing</li>
          <li>Right of access to your personal data</li>
          <li>Right to rectification of inaccurate data</li>
          <li>Right to erasure (&quot;right to be forgotten&quot;)</li>
          <li>Right to restrict processing</li>
          <li>Right to data portability</li>
          <li>Right to object to processing</li>
          <li>Rights related to automated decision-making</li>
        </ul>
        <p>
          You also have the right to lodge a complaint with your local data
          protection authority.
        </p>
      </section>

      <section id="us-users">
        <h2>11. US Users</h2>
        <p>
          If you are located in the United States, you may have additional rights
          under state privacy laws:
        </p>
        <ul>
          <li>
            <strong>California (CCPA):</strong> Right to know, delete, and
            opt-out of sale of personal information.
          </li>
          <li>
            <strong>Virginia (VCDPA):</strong> Rights to access, correct,
            delete, and opt-out of processing.
          </li>
          <li>
            <strong>Colorado (CPA):</strong> Rights to access, correct, delete,
            and opt-out of processing.
          </li>
          <li>
            <strong>Connecticut (CTDPA):</strong> Rights to access, correct,
            delete, and opt-out of processing.
          </li>
        </ul>
        <p>
          We do not sell personal information as defined under applicable state
          laws.
        </p>
      </section>

      <section id="israeli-users">
        <h2>12. Israeli Users</h2>
        <p>
          If you are located in Israel, you have rights under the Protection of
          Privacy Law:
        </p>
        <ul>
          <li>Right to access your personal information</li>
          <li>Right to correct inaccurate information</li>
          <li>Right to delete information in certain circumstances</li>
          <li>Right to object to processing</li>
          <li>Right to data portability</li>
        </ul>
        <p>
          You may contact the Privacy Protection Authority if you have concerns
          about our data processing practices.
        </p>
      </section>

      <section id="updates">
        <h2>13. Updates to This Policy</h2>
        <p>
          We may update this privacy policy from time to time to reflect changes
          in our practices or applicable laws. We will notify you of any material
          changes by posting the updated policy on our platform and updating the
          &quot;Last updated&quot; date. We encourage you to review this policy
          periodically to stay informed about how we protect your information.
        </p>
      </section>

      <section id="contact">
        <h2>14. Contact Us</h2>
        <p>
          If you have any questions about this privacy policy or our data
          practices, please contact us:
        </p>
        <p>
          <strong>Maskanh Inc.</strong>
        </p>
        <ul className="legal-contact-list">
          <li>
            Email:{" "}
            <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
          </li>
          <li>
            Phone:{" "}
            <a href={CONTACT.whatsappUrl}>{CONTACT.phone}</a>
          </li>
          <li>
            Website:{" "}
            <a href={CONTACT.website} target="_blank" rel="noopener noreferrer">
              www.maskanh.com
            </a>
          </li>
        </ul>
        <p>
          For data protection inquiries, you can also contact our Data
          Protection Officer at{" "}
          <a href="mailto:maskanh2023@gmail.com">privacy@maskanh2023.com</a>.
        </p>
      </section>
    </PageLayout>
  );
}
